import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {Button} from "react-bootstrap";
import {getUpcomingTasks} from "../../services/RequestService";
import "./UpcomingTasksPopups.css";

const DAYS_AHEAD = 5;
const STORAGE_KEY = "upcomingTasks";
const MIN_KEY = "upcomingTasksMinimized";

function formatDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("pt-BR");
}

// Mesma lógica de urgência do CardActionComp (calendar): warning -> attention -> danger.
function urgencyClass(task) {
  const now = Date.now();
  const delivery = new Date(task.deliveryDate).getTime();
  const alert = task.alertDate ? new Date(task.alertDate).getTime() : delivery;
  if (now >= delivery) return "utp-danger";
  if (now >= alert) return "utp-attention";
  return "utp-warning";
}

// Pior urgência da lista, para colorir a aba minimizada (danger > attention > warning).
function worstUrgencyClass(tasks) {
  let worst = "utp-warning";
  for (const t of tasks) {
    const c = urgencyClass(t);
    if (c === "utp-danger") return "utp-danger";
    if (c === "utp-attention") worst = "utp-attention";
  }
  return worst;
}

// A lista fica persistida em sessionStorage para sobreviver a QUALQUER navegação
// (inclusive reload) — só some quando o usuário clica em Fechar / Fechar todas.
function loadStored() {
  try {
    const list = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function persist(list) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

// Estado aberto/minimizado também persiste: por padrão inicia minimizado (só a aba),
// para não cobrir a tela/menu ao navegar ou logar.
function loadMinimized() {
  return sessionStorage.getItem(MIN_KEY) !== "false";
}

function persistMinimized(minimized) {
  sessionStorage.setItem(MIN_KEY, minimized ? "true" : "false");
}

export default function UpcomingTasksPopups({decodedToken}) {
  const [tasks, setTasks] = useState(loadStored);
  const [minimized, setMinimized] = useState(loadMinimized);
  const history = useHistory();

  useEffect(() => {
    // Só rebusca quando o usuário acabou de logar (flag setado no controller de login).
    if (!sessionStorage.getItem("showTasksPopup")) return;
    sessionStorage.removeItem("showTasksPopup");

    // Zera a lista do login anterior antes de decidir/rebuscar (evita vazar entre usuários).
    persist([]);
    setTasks([]);
    // Ao logar entra sempre minimizado — só a aba aparece, sem cobrir a tela.
    persistMinimized(true);
    setMinimized(true);

    // Apenas colaboradores (não-clientes) — mesma regra do App.js.
    const isCustomer = decodedToken?.is_customer;
    const isUserAndCustomer = decodedToken?.is_user_and_customer;
    const valid = !isUserAndCustomer ? !isCustomer : true;
    if (!valid) return;

    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + DAYS_AHEAD);

    getUpcomingTasks(start, end)
      .then((res) => {
        if (res.ok) {
          const list = Array.isArray(res.data) ? res.data : [];
          persist(list);
          setTasks(list);
        }
      })
      .catch((e) => console.log("Erro ao carregar tarefas do usuário", e));
  }, [decodedToken]);

  if (tasks.length === 0) return null;

  const close = (id) =>
    setTasks((prev) => {
      const next = prev.filter((t) => t.id !== id);
      persist(next);
      return next;
    });

  const closeAll = (e) => {
    if (e) e.stopPropagation();
    persist([]);
    setTasks([]);
  };

  const toggleMinimized = () =>
    setMinimized((prev) => {
      const next = !prev;
      persistMinimized(next);
      return next;
    });

  const open = (task) => {
    if (task.processId) {
      // Ao abrir um processo, recolhe pra aba para não atrapalhar a navegação.
      persistMinimized(true);
      setMinimized(true);
      history.push(`/processo/${task.processId}/processos`);
    }
  };

  return (
    // column-reverse: a aba (1º filho) fica no rodapé (âncora) e os cards sobem acima dela.
    <div className={"utp-container" + (minimized ? " utp-container-min" : "")}>
      {/* aba/handle — 1º no DOM => rodapé. Clique alterna abrir/minimizar. */}
      <div
        className={"utp-header " + worstUrgencyClass(tasks) + (minimized ? " utp-minimized" : "")}
        onClick={toggleMinimized}
        role="button"
        title={minimized ? "Abrir tarefas" : "Minimizar"}
      >
        <span className="utp-header-title">
          <i className="far fa-clock mr-2"/>
          Tarefas — próximos {DAYS_AHEAD} dias ({tasks.length})
        </span>
        <span className="utp-header-actions">
          {minimized ? (
            <i className="fas fa-chevron-up utp-caret"/>
          ) : (
            <>
              <i className="fas fa-chevron-down utp-caret"/>
              <button type="button" className="utp-close-all" onClick={closeAll}>
                Fechar todas
              </button>
            </>
          )}
        </span>
      </div>

      {!minimized &&
        tasks.map((task) => (
          <div key={task.id} className={"utp-card " + urgencyClass(task)}>
            <div className="utp-card-date">
              <i className="far fa-calendar-check mr-1"/>
              {formatDate(task.deliveryDate)}
            </div>
            <div className="utp-card-task">{task.taskName || "Tarefa"}</div>
            <div className="utp-card-meta">
              <span className="utp-card-customer">{task.customerName}</span>
              {task.actionName ? (
                <span className="utp-card-action"> • {task.actionName}</span>
              ) : null}
            </div>
            <div className="utp-card-actions">
              <Button size="sm" variant="primary" onClick={() => open(task)}>
                <i className="fas fa-external-link-alt mr-1"/> Abrir
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => close(task.id)}>
                Fechar
              </Button>
            </div>
          </div>
        ))}
    </div>
  );
}
